import meetingLocationResponseToLocationPersonaType from 'owa-location-common-well/lib/utils/meetingLocationResponseToLocationPersonaType';
import { PlacesSource } from 'owa-calendar-scheduling-service/lib/schema/PlacesSource';
import findPlacesResponseToLocationSuggestions from './findPlacesResponseToLocationSuggestions';
import store from 'owa-feedback-manager-store/lib/store/locationFeedbackManagerStore';
import transactionComplete from 'owa-feedback-manager-actions/lib/actions/transactionComplete';
import { findMeetingLocations } from 'owa-calendar-scheduling-service/lib/services/findMeetingLocationsService';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isNullOrWhiteSpace } from 'owa-string-utils';
import { LocationPersonaControlViewState } from 'owa-location-persona';
import findWeatherLocations from '../services/findWeatherLocations';
import weatherLocationSuggestionsResponseToLocationPersonaType from './weatherLocationSuggestionsResponseToLocationPersonaType';
import SearchMode from '../store/schema/SearchMode';
import { logServiceCallForDiagnostics } from 'owa-calendar-forms-diagnostics';
import RecurrenceType from 'owa-service/lib/contract/RecurrenceType';
import LocationPersonaType from 'owa-calendar-scheduling-service/lib/schema/LocationPersonaType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { OwaDateRange } from 'owa-datetime-utils/lib/OwaDateRange';
import { isConsumer } from 'owa-session-store';

const MAX_ENTRIES_TYPEDOWN = 5;
const MAX_QUERY_STRING_LENGTH = 255;

/**
 * fetches the location suggetsions from server.
 * @param {string} ownerSmtpAddress: The smtp address of the user
 * @param {string} queryString: the trimmed query string from the location well
 * @param {string[]} requiredAttendees: the required attendees for the meeting. Used by the find places server command to provide relavant results.
 * @param {string[]} optionalAttendees: optional attendees for the meeting. Used by the find places server command to provide relevant results.
 * @param {TimeSlot} timeSlot: the meeting time. Used by the find places server command to provide relevent results
 * @param {boolean} requestRoomsOnly: boolean indicating if request should return only conference rooms
 */
export default async function findLocation(
    ownerSmtpAddress: string,
    queryString: string,
    requiredAttendees: string[] = [],
    optionalAttendees: string[] = [],
    timeSlot?: OwaDateRange,
    currentDevicePosition?: GeolocationPosition,
    searchMode?: SearchMode,
    recurrence?: RecurrenceType
): Promise<LocationPersonaControlViewState[]> {
    // trim the text
    queryString = queryString ? queryString.trim() : '';

    let locationSuggestions: LocationPersonaControlViewState[] = [];

    if (isNullOrWhiteSpace(queryString) || queryString.length < MAX_QUERY_STRING_LENGTH) {
        const isBusiness = !isConsumer(ownerSmtpAddress);

        let source = getLocationSource(ownerSmtpAddress, queryString, isBusiness);
        let deviceLocation = currentDevicePosition?.coords;

        let availableConferenceRoomsOnly = isBusiness && isNullOrWhiteSpace(queryString);

        let {
            status,
            locationTransactionId,
            findPlacesResult,
        } = await fetchLocationSuggestionsFromServer(
            ownerSmtpAddress,
            queryString,
            source,
            requiredAttendees,
            optionalAttendees,
            timeSlot,
            deviceLocation,
            availableConferenceRoomsOnly,
            searchMode,
            recurrence
        );

        if (status == '200') {
            locationSuggestions = findPlacesResponseToLocationSuggestions(
                findPlacesResult,
                locationTransactionId,
                queryString
            );

            // Log transactionComplete entity feedback
            let locations: string[] = [];
            let relevanceHints: string[] = [];
            if (locationSuggestions) {
                locationSuggestions.forEach(location => {
                    locations.push(location.relevanceData.locationFeedbackId);
                    relevanceHints.push(location.relevanceData.relevanceHint);
                });
            }

            transactionComplete(
                store.feedbackManagerState,
                queryString,
                locations,
                locationTransactionId,
                relevanceHints,
                requiredAttendees,
                optionalAttendees
            );
        }

        let findResultCount = locationSuggestions.length;
        if (findResultCount > 0) {
            // Remove additional results so that only MAX_ENTRIES_TYPEDOWN number of locations are shown.
            if (findResultCount > MAX_ENTRIES_TYPEDOWN) {
                locationSuggestions.splice(
                    MAX_ENTRIES_TYPEDOWN,
                    findResultCount - MAX_ENTRIES_TYPEDOWN
                );
            }
        }
    }
    return locationSuggestions;
}

function getLocationSource(
    ownerSmtpAddress: string,
    queryString: string,
    isBusiness: boolean
): PlacesSource {
    let defaultPlaceSource: PlacesSource = isFeatureEnabled('cal-mf-locationShowNearByRooms')
        ? PlacesSource.Proximity
        : PlacesSource.Relevance;

    const isNonEmptyEmptyQuery = !isNullOrWhiteSpace(queryString);
    // For blank query use relevance location, otherwise use both bing locations and relevance
    // and only use bing locations when the user configuration of PlacesEnabled is true
    let useBingLocation =
        isNonEmptyEmptyQuery && getUserConfiguration().PolicySettings.PlacesEnabled;

    let source = useBingLocation
        ? PlacesSource.Relevance | PlacesSource.BingAutosuggest
        : defaultPlaceSource;

    // Use Directory as a source for non zero-query suggestions if flight is enabled.
    let useTenantSearch = isBusiness && isNonEmptyEmptyQuery;
    if (useTenantSearch) {
        source = source | PlacesSource.Directory;
    }

    return source;
}

/** create the fetch suggesttion request, posts it and process the results*/
async function fetchLocationSuggestionsFromServer(
    ownerSmtpAddress: string,
    queryString: string,
    source: PlacesSource,
    requiredAttendees: string[] = [],
    optionalAttendees: string[] = [],
    timeSlot?: OwaDateRange,
    deviceLocation?: GeolocationCoordinates,
    availableConferenceRoomsOnly?: boolean,
    searchMode?: SearchMode,
    recurrence?: RecurrenceType
) {
    let response;
    let locationTransactionId: string;
    let findPlacesResult: LocationPersonaType[] = [];
    let findMeetingLocationResponse;

    if (searchMode == 'OnlyCities') {
        response = await findWeatherLocations(queryString, MAX_ENTRIES_TYPEDOWN, deviceLocation);

        if (response && response.status == 200) {
            locationTransactionId = response.headers.get('request-id');
            let weatherLocationsResponse = {
                Suggestions: (await response.json()).suggestions,
            };
            findPlacesResult = weatherLocationSuggestionsResponseToLocationPersonaType(
                weatherLocationsResponse
            );
        }
    } else {
        const queryLAS = async (availableConferenceRoomsOnly: boolean) => {
            response = await findMeetingLocations(
                ownerSmtpAddress,
                queryString,
                source,
                requiredAttendees,
                optionalAttendees,
                MAX_ENTRIES_TYPEDOWN,
                timeSlot,
                deviceLocation,
                null,
                availableConferenceRoomsOnly,
                searchMode == 'OnlyRooms' /* requestRoomsOnly */,
                recurrence
            );

            if (response && response.status == 200) {
                locationTransactionId = response.headers.get('request-id');
                findMeetingLocationResponse = await response.json();
            }
        };

        await queryLAS(availableConferenceRoomsOnly);

        // If we requested only available conference rooms and no response was returned for zero query.
        // We will re-request the location suggestions without "availableConferenceRoomsOnly" param
        if (
            availableConferenceRoomsOnly &&
            findMeetingLocationResponse &&
            findMeetingLocationResponse.MeetingLocations.length == 0
        ) {
            await queryLAS(false /* availableConferenceRoomsOnly */);
        }

        findPlacesResult = meetingLocationResponseToLocationPersonaType(
            findMeetingLocationResponse
        );
    }

    // Log the service call to forms diagnostics panel
    if (isFeatureEnabled('cal-mf-diagnostics')) {
        let headers = {};
        // We have to convert response.headers (which is an iterator) into a headers json object
        // headers could not be set if the request fail
        if (response?.headers) {
            for (let header of response.headers.entries()) {
                // header[0] is the key and header[1] is the value of the header
                // For example, header[0] = 'x-backendhttpstatus' and header[1] = '200'
                headers[header[0]] = header[1];
            }
        }

        logServiceCallForDiagnostics({
            action: 'findmeetinglocations',
            status: response ? response.status : 'Error',
            response: findPlacesResult,
            request: {
                queryString: queryString,
                source: source,
                requiredAttendees: requiredAttendees,
                optionalAttendees: optionalAttendees,
                timeSlot: timeSlot,
                deviceLocation: deviceLocation,
                availableConferenceRoomsOnly: availableConferenceRoomsOnly,
            },
            headers: headers,
        });
    }

    return {
        status: response ? response.status : 'Error',
        locationTransactionId: locationTransactionId,
        findPlacesResult: findPlacesResult,
    };
}
